import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { Card, PageHeader, Btn, Input, Select, Field, Modal, Table, Spinner, ErrBox } from '../../../components/UI';
import { FilterBar, buildQS, initFilters } from '../../../components/FilterBar';
import Badge from '../../../components/Badge';
import { fmtRp, fmtDate } from '../../../utils/format';
import { assetsAPI, masterAPI } from '../../../utils/api';

const levelBadge = (val, color) => val ? (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600, background:`${color}22`, color, border:`1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

const AssetListTab = () => {
  const [assets, setAssets] = useState([]);
  const [filters, setFilters] = useState(initFilters());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', category_id: '', acquisition_value: '',
    acquisition_date: '', condition: 'Baik', unit_id: '', location: '',
    vendor_id: '', anggaran_id: '', payment_request_id: '', sppa_no: '', akun_id: '', warehouse_id: '', coa_detail_id: ''
  });
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [anggaranList, setAnggaranList] = useState([]);
  const [refUnit, setRefUnit] = useState([]);
  const [refAkun, setRefAkun] = useState([]);
  const [refWarehouse, setRefWarehouse] = useState([]);
  const [refCoaDetail, setRefCoaDetail] = useState([]);
  const [budgetWarning, setBudgetWarning] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const selectStyle = { padding: "8px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", background: "white", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

  const loadDashboard = async () => {
    try {
      const r = await assetsAPI.getDashboard();
      setDashboard(r.data || {});
    } catch {}
  };

  const loadAssets = async (f = filters, s = search) => {
    setLoading(true);
    setErr('');
    try {
      const qs = buildQS(f, s);
      const r = await assetsAPI.getAll(qs);
      setAssets(r.data || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const loadRefs = async () => {
    try {
      const [c, v, a, u, ak, wh, cd] = await Promise.all([
        assetsAPI.getCategories(),
        assetsAPI.getVendors(),
        assetsAPI.getAnggaranAvailable(),
        masterAPI.getUnit(),
        masterAPI.getCoaAkun(),
        masterAPI.getWarehouse(),
        masterAPI.getCoaDetailAkun(),
      ]);
      setCategories((c.data||[]).map(x=>({ value:x.id, label:x.name })));
      setVendors((v.data||[]).map(x=>({ value:x.id, label:x.nama })));
      setAnggaranList((a.data||[]).map(x=>({ value:x.id, label:`${x.tahun_anggaran} - Sisa: Rp ${(x.sisa_pagu||0).toLocaleString('id-ID')}` })));
      setRefUnit((u.data||[]).map(x=>({ value:x.id, label:x.nama })));
      setRefAkun((ak.data||[]).map(x=>({
        value: x.id,
        label: `${x.kode} - ${x.nama}`,
        program_kode: x.program_kode, program_nama: x.program_nama,
        kegiatan_kode: x.kegiatan_kode, kegiatan_nama: x.kegiatan_nama,
        kro_kode: x.kro_kode, kro_nama: x.kro_nama,
      })));
      setRefWarehouse((wh.data||[]).map(x=>({ value: x.id, label: `${x.kode} - ${x.nama}` })));
      setRefCoaDetail((cd.data||[]).map(x=>({ value: x.id, label: `${x.kode} - ${x.nama}`, akun_id: x.akun_id })));
    } catch {}
  };

  useEffect(() => { loadDashboard(); loadAssets(filters, search); }, [filters, search]);
  useEffect(() => { loadRefs(); }, []);

  const checkBudget = async (anggaranId, value) => {
    if (!anggaranId || !value) { setBudgetWarning(null); return; }
    const ang = anggaranList.find(a => a.value === anggaranId);
    if (ang && Number(value) > (ang.sisa_pagu || 0)) {
      setBudgetWarning(`Pagu tidak mencukupi! Sisa: Rp ${(ang.sisa_pagu||0).toLocaleString('id-ID')}`);
    } else {
      setBudgetWarning(null);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'anggaran_id') checkBudget(value, prev.acquisition_value);
      if (field === 'acquisition_value') checkBudget(prev.anggaran_id, value);
      if (field === 'akun_id') return { ...updated, akun_id: value, coa_detail_id: '', _selectedAkun: null };
      return updated;
    });
  };

  const filteredCoaDetail = form.akun_id ? refCoaDetail.filter(d => String(d.akun_id) === String(form.akun_id)) : [];
  const selectedCoaDetail = refCoaDetail.find(d => d.value === form.coa_detail_id);

  useEffect(() => {
    if (refCoaDetail.length > 0 && form.coa_detail_id) {
      const exists = refCoaDetail.some(d => d.value === form.coa_detail_id);
      if (!exists) setForm(prev => ({ ...prev, coa_detail_id: '' }));
    }
  }, [refCoaDetail]);

  useEffect(() => {
    if (refAkun.length > 0 && form.akun_id && !form._selectedAkun) {
      const a = refAkun.find(x => x.value === form.akun_id);
      if (a) setForm(prev => ({ ...prev, _selectedAkun: { program_kode: a.program_kode, program_nama: a.program_nama, kegiatan_kode: a.kegiatan_kode, kegiatan_nama: a.kegiatan_nama, kro_kode: a.kro_kode, kro_nama: a.kro_nama } }));
    }
  }, [refAkun, form.akun_id]);

  const selectedAkun = form._selectedAkun || refAkun.find(x => x.value === form.akun_id);

  const handleSave = async () => {
    if (!form.code || !form.name || !form.category_id || !form.acquisition_value || !form.acquisition_date)
      return alert('Semua field yang ditandai wajib diisi');
    if (budgetWarning && !window.confirm(`Warning: ${budgetWarning}. Lanjutkan?`)) return;
    setSaving(true);
    try {
      if (editId) {
        await assetsAPI.update(editId, form);
      } else {
        await assetsAPI.create(form);
      }
      setShowModal(false);
      setEditId(null);
      setForm({ code: '', name: '', category_id: '', acquisition_value: '', acquisition_date: '', condition: 'Baik', unit_id: '', location: '', vendor_id: '', anggaran_id: '', payment_request_id: '', sppa_no: '', akun_id: '', warehouse_id: '', coa_detail_id: '', _selectedAkun: null });
      setBudgetWarning(null);
      loadAssets();
      loadDashboard();
    } catch (e) { alert("Gagal menyimpan: " + e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (asset) => {
    setEditId(asset.id);
    setForm({
      code: asset.code,
      name: asset.name,
      category_id: asset.category_id || '',
      acquisition_value: asset.acquisition_value,
      acquisition_date: asset.acquisition_date?.split('T')[0],
      condition: asset.condition || 'Baik',
      unit_id: asset.unit_id || '',
      location: asset.location || '',
      vendor_id: asset.vendor_id || '',
      anggaran_id: asset.anggaran_id || '',
      payment_request_id: asset.payment_request_id || '',
      sppa_no: asset.sppa_no || '',
      akun_id: asset.akun_id || '',
      warehouse_id: asset.warehouse_id || '',
      coa_detail_id: asset.coa_detail_id || '',
      _selectedAkun: asset.akun_id ? (() => {
        const a = refAkun.find(x => x.value === asset.akun_id);
        return a ? { program_kode: a.program_kode, program_nama: a.program_nama, kegiatan_kode: a.kegiatan_kode, kegiatan_nama: a.kegiatan_nama, kro_kode: a.kro_kode, kro_nama: a.kro_nama } : null;
      })() : null,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus aset ini?')) return;
    try {
      await assetsAPI.delete(id);
      loadAssets();
      loadDashboard();
    } catch (e) { alert("Gagal menghapus: " + e.message); }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm({ code: '', name: '', category_id: '', acquisition_value: '', acquisition_date: '', condition: 'Baik', unit_id: '', location: '', vendor_id: '', anggaran_id: '', payment_request_id: '', sppa_no: '', akun_id: '', warehouse_id: '', coa_detail_id: '', _selectedAkun: null });
    setBudgetWarning(null);
  };

  return (
    <div>
      <FilterBar filters={filters} onChange={setFilters} search={search} onSearch={setSearch}
        searchPlaceholder="Cari kode atau nama aset…">
      </FilterBar>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:20 }}>
        <Card style={{ borderLeft:"4px solid #2563EB", padding:"14px 18px" }}>
          <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase" }}>Total Aset</p>
          <p style={{ margin:0, fontSize:22, fontWeight:700, color:"#1E40AF" }}>{dashboard?.total_assets || 0}</p>
          <p style={{ margin:"2px 0 0", fontSize:11, color:"#9CA3AF" }}>unit</p>
        </Card>
        <Card style={{ borderLeft:"4px solid #059669", padding:"14px 18px" }}>
          <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase" }}>Nilai Perolehan</p>
          <p style={{ margin:0, fontSize:18, fontWeight:700, color:"#047857" }}>{fmtRp(dashboard?.total_acquisition_value || 0)}</p>
          <p style={{ margin:"2px 0 0", fontSize:11, color:"#9CA3AF" }}>total</p>
        </Card>
        <Card style={{ borderLeft:"4px solid #7C3AED", padding:"14px 18px" }}>
          <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase" }}>Total Depresiasi</p>
          <p style={{ margin:0, fontSize:18, fontWeight:700, color:"#6D28D9" }}>{fmtRp(dashboard?.total_depreciation || 0)}</p>
          <p style={{ margin:"2px 0 0", fontSize:11, color:"#9CA3AF" }}>akumulasi</p>
        </Card>
        <Card style={{ borderLeft:"4px solid #F59E0B", padding:"14px 18px" }}>
          <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase" }}>Nilai Buku</p>
          <p style={{ margin:0, fontSize:18, fontWeight:700, color:"#D97706" }}>{fmtRp((dashboard?.total_acquisition_value||0) - (dashboard?.total_depreciation||0))}</p>
          <p style={{ margin:"2px 0 0", fontSize:11, color:"#9CA3AF" }}>saat ini</p>
        </Card>
      </div>

      <Card>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:"#111827" }}>Daftar Aset BMN ({assets.length})</h3>
          <Btn icon={<Plus size={14}/>} onClick={()=>setShowModal(true)}>Tambah Aset</Btn>
        </div>
        {err && <div style={{marginBottom:12}}><ErrBox msg={err} onRetry={()=>loadAssets()}/></div>}
        {loading ? <Spinner/> : (
          <Table pageSize={10} cols={[
            { key:"code",      label:"Kode Aset", nowrap:true },
            { key:"name",      label:"Nama Aset" },
            { key:"category_name", label:"Kategori" },
            { key:"program_kode", label:"Program", render:(v,r)=><ProgramBadge v={v}/> },
            { key:"kegiatan_kode", label:"Kegiatan", render:(v,r)=><KegiatanBadge v={v}/> },
            { key:"kro_kode",   label:"KRO", render:(v,r)=><KroBadge v={v}/> },
            { key:"kode_akun",  label:"Akun", render:(v,r)=>(
              <div>
                <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#374151"}}>{v || '-'}</span>
                <br/><span style={{fontSize:11,color:"#6B7280"}}>{r.nama_akun || '-'}</span>
              </div>
            )},
            { key:"vendor_nama", label:"Vendor" },
            { key:"warehouse_nama", label:"Gudang", render:v=>v||<span style={{color:"#9CA3AF"}}>-</span> },
            { key:"acquisition_value", label:"Nilai", align:"right",
              render:v=><span style={{fontWeight:700,fontFamily:"monospace",fontSize:12,color:"#1E40AF"}}>{fmtRp(v)}</span> },
            { key:"condition", label:"Kondisi", render:v=>(
              <span style={{padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:600,
                background: v==='Baik'?'#ECFDF5':v==='Sedang'?'#FFFBEB':'#FEE2E2',
                color: v==='Baik'?'#047857':v==='Sedang'?'#B45309':'#991B1B'
              }}>{v}</span>
            )},
            { key:"status",   label:"Status", render:v=><Badge status={v}/> },
            { key:"id", label:"Aksi", render:(id,row)=>(
              <div style={{display:"flex",gap:6}}>
                <Btn size="sm" variant="secondary" onClick={()=>handleEdit(row)}>Edit</Btn>
                <Btn size="sm" variant="danger" onClick={()=>handleDelete(id)}>Hapus</Btn>
              </div>
            )},
          ]} data={assets}/>
        )}
      </Card>

      {showModal && (
        <Modal title={editId ? "Edit Aset" : "Tambah Aset Baru"} onClose={closeModal} wide>
          {budgetWarning && (
            <div style={{ padding:"10px 14px", background:"#FEF2F2", border:"1px solid #DC2626", borderRadius:8, color:"#DC2626", marginBottom:16, fontSize:13 }}>
              {budgetWarning}
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Kode Aset" required>
              <Input value={form.code} onChange={e=>setForm({...form,code:e.target.value})} placeholder="AST-2026-001"/>
            </Field>
            <Field label="Nama Aset" required>
              <Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Nama aset"/>
            </Field>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Kategori" required>
              <Select value={form.category_id} onChange={e=>handleFormChange('category_id', e.target.value)} options={categories} placeholder="Pilih kategori…"/>
            </Field>
            <Field label="Kondisi">
              <Select value={form.condition} onChange={e=>setForm({...form,condition:e.target.value})}
                options={["Baik","Sedang","Rusak","Hilang"].map(v=>({value:v,label:v}))}/>
            </Field>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Nilai Perolehan (Rp)" required>
              <Input type="number" value={form.acquisition_value} onChange={e=>handleFormChange('acquisition_value', e.target.value)} placeholder="0"/>
            </Field>
            <Field label="Tanggal Perolehan" required>
              <Input type="date" value={form.acquisition_date} onChange={e=>setForm({...form,acquisition_date:e.target.value})}/>
            </Field>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Vendor">
              <Select value={form.vendor_id} onChange={e=>setForm({...form,vendor_id:e.target.value})} options={vendors} placeholder="Pilih vendor…"/>
            </Field>
            <Field label="No. Kontrak/SPPA">
              <Input value={form.sppa_no} onChange={e=>setForm({...form,sppa_no:e.target.value})} placeholder="SPPA-2026-001"/>
            </Field>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Anggaran (DIPA)">
              <Select value={form.anggaran_id} onChange={e=>handleFormChange('anggaran_id', e.target.value)} options={anggaranList} placeholder="Tanpa anggaran"/>
            </Field>
            <Field label="Akun MA">
              <Select value={form.akun_id} onChange={e=>handleFormChange('akun_id', e.target.value)} options={refAkun} placeholder="Pilih akun…"/>
            </Field>
          </div>
          {selectedAkun && (
            <div style={{ marginBottom:16, padding:10, background:"#F8FAFC", borderRadius:8, fontSize:12, display:"flex", gap:6, flexWrap:"wrap" }}>
              <ProgramBadge v={selectedAkun.program_kode ? `${selectedAkun.program_kode} - ${selectedAkun.program_nama}` : null}/>
              <KegiatanBadge v={selectedAkun.kegiatan_kode ? `${selectedAkun.kegiatan_kode} - ${selectedAkun.kegiatan_nama}` : null}/>
              <KroBadge v={selectedAkun.kro_kode ? `${selectedAkun.kro_kode} - ${selectedAkun.kro_nama}` : null}/>
            </div>
          )}
          {form.akun_id && (
            <Field label="Detail COA">
              <Select value={form.coa_detail_id} onChange={e=>setForm({...form,coa_detail_id:e.target.value})} options={refCoaDetail.length > 0 ? filteredCoaDetail : []} placeholder={refCoaDetail.length === 0 ? "Memuat..." : "Pilih Detail COA…"}/>
            </Field>
          )}
          {selectedCoaDetail && (
            <div style={{ marginBottom:16, padding:10, background:"#EFF6FF", borderRadius:8, fontSize:12, color:"#1D4ED8" }}>
              {selectedCoaDetail.label}
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Unit">
              <Select value={form.unit_id} onChange={e=>setForm({...form,unit_id:e.target.value})} options={refUnit} placeholder="Pilih unit…"/>
            </Field>
            <Field label="Gudang / Lokasi">
              <Select value={form.warehouse_id} onChange={e=>setForm({...form,warehouse_id:e.target.value})} options={[{value:'',label:'-- Pilih Gudang --'}, ...refWarehouse]} placeholder="Pilih gudang…"/>
            </Field>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:16 }}>
            <Field label="Detail Lokasi">
              <Input value={form.location} onChange={e=>setForm({...form,location:e.target.value})} placeholder="Ruang / Rak / Lantai, contoh: Ruang Server Lantai 2"/>
            </Field>
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:20 }}>
            <Btn variant="secondary" onClick={closeModal}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving?"Menyimpan…":"Simpan"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AssetListTab;